import { Injectable } from '@angular/core'
import { ViewerService } from './viewer.service'
import {
    combineLatest,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    take,
} from 'rxjs'
import { GenericLayer } from '../../shared/model/layers/generic-layer'
import {
    isAnnotationLayer,
    isImageLayer,
    isSegmentationLayer,
} from '../../shared/model/layers/layer-types'
import { ImageLayer } from '../../shared/model/layers/image-layer'
import {
    NeuroglancerAnnotationUserLayer,
    NeuroglancerImageUserLayer,
    NeuroglancerSegmentationUserLayer,
} from '../../shared/model/neuroglancer-type-mappings'
import { SegmentationLayer } from '../../shared/model/layers/segmentation-layer'
import { environment } from '../../../environments/environment'
import { AnnotationLayer } from '../../shared/model/layers/annotation-layer'
import { TrackableService } from './trackable.service'
import { Store } from '@ngrx/store'
import {
    selectLayerInfos,
    selectLayerGroups,
    selectLayerInfosForGroupId,
    selectLayers,
    selectInvisibleLayers,
} from '../../state/viewer/viewer.selectors'
import { LayerConfig, LayerGroup } from '../../state/viewer/viewer.state'
import { isManagedLayer } from '../../state/viewer/viewer-layer-utils'

@Injectable({
    providedIn: 'root',
})
export class ViewerLayersService {
    private hiddenSegmentationLayers: GenericLayer[] = []
    private spaceKeyPressed = false

    constructor(
        private readonly viewer: ViewerService,
        private readonly trackableService: TrackableService,
        private readonly store: Store
    ) {}

    private readonly imageLayers$: Observable<GenericLayer[]> = this.viewer.layers$.pipe(
        map(layers =>
            layers
                .filter(layer => isImageLayer(layer))
                .map(
                    layer =>
                        new ImageLayer(
                            layer.layer as NeuroglancerImageUserLayer,
                            this.store,
                            this.trackableService
                        )
                )
        )
    )

    private readonly segmentationLayers$: Observable<GenericLayer[]> = this.viewer.layers$.pipe(
        map(layers =>
            layers
                .filter(layer => isSegmentationLayer(layer))
                .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                .map(
                    layer =>
                        new SegmentationLayer(
                            layer.layer as NeuroglancerSegmentationUserLayer,
                            this.store,
                            this.trackableService
                        )
                )
        )
    )

    private readonly annotationLayers$: Observable<GenericLayer[]> = this.viewer.layers$.pipe(
        map(layers =>
            layers.filter(
                layer =>
                    isAnnotationLayer(layer) &&
                    layer.name !== environment.selectionAnnotationLayerName
            )
        ),
        switchMap(layers => {
            return layers.length > 0
                ? combineLatest(
                      layers
                          .filter(layer => !!layer.layer)
                          .map(layer =>
                              layer.layer!.isReady
                                  ? of(layer)
                                  : this.trackableService
                                        .observeEvent(layer.layer!.readyStateChanged)
                                        .pipe(map(() => layer))
                          )
                  )
                : of([])
        }),
        map(layers =>
            layers.map(
                layer =>
                    new AnnotationLayer(
                        layer.layer as NeuroglancerAnnotationUserLayer,
                        this.store,
                        this.trackableService
                    )
            )
        )
    )

    readonly layers$: Observable<GenericLayer[]> = combineLatest({
        image: this.imageLayers$,
        segmentation: this.segmentationLayers$,
        annotation: this.annotationLayers$,
    }).pipe(map(layers => [...layers.image, ...layers.segmentation, ...layers.annotation]))

    readonly layerGroups$: Observable<ViewerLayerNode[]> = combineLatest({
        groups: this.store.select(selectLayerGroups),
        layerInfos: this.store.select(selectLayerInfos),
        storeLayers: this.store.select(selectLayers),
        invisibleLayers: this.store.select(selectInvisibleLayers),
        loadedLayerIds: this.store.select(selectLayers).pipe(
            map(layers => new Set((layers ?? []).map(l => l.id).filter((id): id is string => !!id)))
        ),
        layers: this.layers$,
    }).pipe(
        map(layerGroupInfo => {
            const allLayersWithLayerId: ViewerLayerWithId[] = layerGroupInfo.layers.map(layer => {
                return {
                    layer,
                    id:
                        layerGroupInfo.layerInfos.find(
                            l => l.name === layer.name && l.source === layer.source
                        )?.id || null,
                    assignedToGroup: false,
                }
            })

            const layerConfigById = new Map<string, LayerConfig>()
            for (const layer of [
                ...(layerGroupInfo.storeLayers ?? []),
                ...(layerGroupInfo.invisibleLayers ?? []),
            ]) {
                if (layer.id) layerConfigById.set(layer.id, layer)
            }

            const layerGroups = [...layerGroupInfo.groups].map(group =>
                addViewerLayersToGroupRecursively(
                    group,
                    allLayersWithLayerId,
                    layerGroupInfo.loadedLayerIds,
                    layerConfigById
                )
            )

            return layerGroups
        })
    )

    getLayersForGroup(groupId: string): Observable<GenericLayer[]> {
        return this.store.select(selectLayerInfosForGroupId(groupId)).pipe(
            switchMap(layerInfos =>
                this.layers$.pipe(
                    map(layers => {
                        return layerInfos
                            .map(info =>
                                layers.find(
                                    layer =>
                                        layer.name === info.name && layer.source === info.source
                                )
                            )
                            .filter((layer: GenericLayer): layer is GenericLayer => !!layer)
                    })
                )
            ),
            take(1),
            shareReplay()
        )
    }

    toggleSegmentationLayersVisibility = (visible: boolean) => {
        if (this.hiddenSegmentationLayers.length === 0 && !visible && !this.spaceKeyPressed) {
            this.spaceKeyPressed = true
            this.segmentationLayers$.pipe(take(1)).subscribe((layers: any) => {
                layers.forEach((layer: any) => {
                    if (layer.baseLayer.visible) {
                        this.hiddenSegmentationLayers.push(layer)
                        layer.setVisible(false)
                    }
                })
            })
        } else if (visible && this.spaceKeyPressed) {
            this.spaceKeyPressed = false
            this.hiddenSegmentationLayers.forEach((layer: GenericLayer) => {
                layer.setVisible(true)
                this.hiddenSegmentationLayers = []
            })
        }
    }
}

const addViewerLayersToGroupRecursively = (
    layerGroup: LayerGroup,
    viewerLayers: ViewerLayerWithId[],
    loadedLayerIds: Set<string>,
    layerConfigById: Map<string, LayerConfig>
): ViewerLayerNode => {
    const loadedLayers = layerGroup.layerIds
        .filter(layerId => loadedLayerIds.has(layerId))
        .map(layerId => {
            const layerWithId = viewerLayers.find(ll => ll.id === layerId)
            if (layerWithId) layerWithId.assignedToGroup = true
            return layerWithId
        })
        .map(l => l?.layer)
        .filter((l): l is GenericLayer => !!l)
        .map(l => ({ layer: l } as ViewerLayerNode))

    // Match the manage-layers dialog: only count managed (non-selection) layers.
    const unloadedLayerCount = layerGroup.layerIds.filter(layerId => {
        const config = layerConfigById.get(layerId)
        if (!config || !isManagedLayer(config)) return false
        return !loadedLayerIds.has(layerId)
    }).length

    return {
        ...layerGroup,
        unloadedLayerCount,
        children: [
            ...loadedLayers,
            ...layerGroup.children.map(child =>
                addViewerLayersToGroupRecursively(
                    child,
                    viewerLayers,
                    loadedLayerIds,
                    layerConfigById
                )
            ),
        ],
    }
}

type ViewerLayerWithId = {
    layer: GenericLayer
    id?: string | null | undefined
    assignedToGroup: boolean
}

type ViewerLayerInvisible = {
    id: string
    name: string
    source: string
    type: string
    visible: boolean
}

export type ViewerLayerNode = {
    id?: string
    name?: string
    layer?: GenericLayer | ViewerLayerInvisible | null
    children?: ViewerLayerNode[] | null
    layerIds?: string[]
    /** Unloaded managed layers in this group (excludes selection annotation layers). */
    unloadedLayerCount?: number
}
